import { ClipboardPageLabel, ClipboardPageLabelRef } from "routes/lens-editor/page-labels/ClipboardPageLabel";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { FocusEvent, forwardRef, useImperativeHandle, useState } from "react";
import DebouncedText from "components/DebouncedText";
import theme from "theme";
import produce from "immer";

interface RuleCellProps {
  value: string | number | undefined | null;
  onChange: (newValue: string | number) => void;
  format?: (v: any) => any;
}
function RuleCell({ value, onChange, format = (v) => v }: RuleCellProps) {
  const [editing, setEditing] = useState(false);
  const onFocus = (event: FocusEvent<any>) => {
    event.preventDefault();
    const { target } = event;
    target.focus();
    target.setSelectionRange(0, target.value.length);
  };

  return editing ? (
    <TableCell>
      <TextField onFocus={onFocus} value={value} onChange={(evt) => onChange(format(evt.target.value))} />
    </TableCell>
  ) : (
    <TableCell onClick={() => setEditing(true)}>{value}</TableCell>
  );
}

const EditableClipboardPageLabel = forwardRef<ClipboardPageLabelRef, ClipboardPageLabel>(
  function EditableClipboardPageLabel(pageLabel: ClipboardPageLabel, ref) {
    const [name, setName] = useState(pageLabel.label.name);
    const [preventsLensHits, setPreventLensHits] = useState<boolean>(pageLabel.label.preventsLensHits);
    const [triggersFormExtraction, setTriggersFormExtraction] = useState(pageLabel.label.triggersFormExtraction);
    const [triggersTableExtraction, setTriggersTableExtraction] = useState(pageLabel.label.triggersTableExtraction);
    const [rules, setRules] = useState(pageLabel.rules);

    useImperativeHandle(
      ref,
      () => ({
        name,
        preventsLensHits,
        triggersFormExtraction,
        triggersTableExtraction,
        rules,
      }),
      [name, preventsLensHits, triggersFormExtraction, triggersTableExtraction, rules]
    );

    const updateRule = (ruleIndex: number, key: string, value: string | number) => {
      setRules(
        produce((draft) => {
          const rule = draft[ruleIndex];

          if (rule !== undefined) {
            rule[key] = value;
          }
        })
      );
    };

    return (
      <Box
        sx={{ p: 2, borderRadius: `${theme.shape.borderRadius}px`, border: `1px solid ${theme.palette.grey["100"]}` }}
        ref={ref}>
        <FormControl fullWidth>
          <DebouncedText value={name} onChange={setName} label="Name" />
        </FormControl>
        <Box sx={{ my: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preventsLensHits}
                onChange={(event) => {
                  setPreventLensHits(event.target.checked);
                }}
              />
            }
            label="Prevents Lens Hits"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={triggersFormExtraction}
                onChange={(event) => {
                  setTriggersFormExtraction(event.target.checked);
                }}
              />
            }
            label="Triggers Form Extraction"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={triggersTableExtraction}
                onChange={(event) => {
                  setTriggersTableExtraction(event.target.checked);
                }}
              />
            }
            label="Triggers Table Extraction"
          />
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Exclude Matches</TableCell>
              <TableCell>Group</TableCell>
              <TableCell>Query Text</TableCell>
              <TableCell width="75px">Fuzziness</TableCell>
              <TableCell width="50px">Slop</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule, index) => (
              <TableRow key={index}>
                <TableCell>{rule.exclude ? "Yes" : "No"}</TableCell>
                <RuleCell value={rule.groupName} onChange={updateRule.bind(null, index, "groupName")} />
                <RuleCell value={rule.queryText} onChange={updateRule.bind(null, index, "queryText")} />
                <RuleCell value={rule.queryFuzziness} onChange={updateRule.bind(null, index, "queryFuzziness")} />
                <RuleCell
                  value={rule.queryPhraseSlop}
                  onChange={updateRule.bind(null, index, "queryPhraseSlop")}
                  format={(v) => parseInt(v)}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }
);

export default EditableClipboardPageLabel;
